// src/components/WorkspaceEmailTemplates.tsx

// What? Workspace-level email template editor filtered to the workspace's enabled module.
// Why? So workspace admins can edit email templates relevant to their module (e.g. myvault,
//      bkg) without needing org-level access.
// How? Uses the workspace-scoped template API (R-6a) so the backend enforces workspace
//      admin permission rather than org admin. Falls back to an empty-state card with a
//      "Seed defaults" action when no templates exist for the workspace yet (R-7).

import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Stack,
  Alert,
  Table,
  Sheet,
  Chip,
  IconButton,
  Divider,
  CircularProgress,
} from "@mui/joy";
import EmailIcon from "@mui/icons-material/Email";
import EditIcon from "@mui/icons-material/Edit";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import RestoreIcon from "@mui/icons-material/Restore";
import AddIcon from "@mui/icons-material/Add";
import { useAuthFetch } from "../utils/useAuthFetch";
import EmailTemplateEditor from "./Organization/EmailTemplateEditor";

interface WorkspaceEmailTemplatesProps {
  organizationId: string;
  workspaceId: string;
  moduleFilter: string; // e.g. "myvault"
}

interface TemplateInfo {
  template_key: string;
  module: string;
  display_name: string;
  subject_template: string;
  is_active: boolean;
  is_system: boolean;
  available_variables: { name: string; description: string }[];
}

interface TemplateDetail extends TemplateInfo {
  body_template: string;
}

const WorkspaceEmailTemplates: React.FC<WorkspaceEmailTemplatesProps> = ({
  organizationId,
  workspaceId,
  moduleFilter,
}) => {
  const authFetch = useAuthFetch();
  const [templates, setTemplates] = useState<TemplateInfo[]>([]);
  const [editing, setEditing] = useState<TemplateDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [feedback, setFeedback] = useState<{
    type: "success" | "danger";
    message: string;
  } | null>(null);

  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const data = await authFetch(
        `/api/email/workspace/${workspaceId}/templates`,
        "fetch email templates",
      );
      if (Array.isArray(data)) {
        // Filter to the workspace's module
        setTemplates(
          data.filter((t: TemplateInfo) => t.module === moduleFilter),
        );
      }
    } catch {
      // Templates not available
    } finally {
      setLoading(false);
    }
  }, [authFetch, workspaceId, moduleFilter]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const handleEdit = async (templateKey: string) => {
    try {
      const data = await authFetch(
        `/api/email/workspace/${workspaceId}/templates/${templateKey}`,
        "load template detail",
      );
      setEditing(data as TemplateDetail);
    } catch (err) {
      setFeedback({
        type: "danger",
        message: `Failed to load template: ${err}`,
      });
    }
  };

  const handleSave = async () => {
    if (!editing) return;
    setSaving(true);
    setFeedback(null);
    try {
      await authFetch(
        `/api/email/workspace/${workspaceId}/templates/${editing.template_key}`,
        "save template",
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            subject_template: editing.subject_template,
            body_template: editing.body_template,
            is_active: editing.is_active,
          }),
        },
      );
      setFeedback({ type: "success", message: "Template saved" });
      await loadTemplates();
    } catch (err) {
      setFeedback({ type: "danger", message: `Failed to save: ${err}` });
    } finally {
      setSaving(false);
      setTimeout(() => setFeedback(null), 4000);
    }
  };

  const handleReset = async () => {
    if (!editing) return;
    setSaving(true);
    setFeedback(null);
    try {
      await authFetch(
        `/api/email/workspace/${workspaceId}/templates/${editing.template_key}`,
        "reset template",
        { method: "DELETE" },
      );
      setFeedback({ type: "success", message: "Template reset to default" });
      setEditing(null);
      await loadTemplates();
    } catch (err) {
      setFeedback({ type: "danger", message: `Failed to reset: ${err}` });
    } finally {
      setSaving(false);
      setTimeout(() => setFeedback(null), 4000);
    }
  };

  const handleSeedDefaults = async () => {
    setSeeding(true);
    setFeedback(null);
    try {
      const result = await authFetch(
        `/api/email/workspace/${workspaceId}/seed-templates?module=${encodeURIComponent(moduleFilter)}`,
        "seed workspace templates",
        { method: "POST" },
      );
      setFeedback({
        type: "success",
        message: `Seeded ${result?.seeded ?? 0} ${moduleFilter} template(s)`,
      });
      await loadTemplates();
    } catch (err) {
      setFeedback({ type: "danger", message: `Failed to seed: ${err}` });
    } finally {
      setSeeding(false);
      setTimeout(() => setFeedback(null), 4000);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress size="sm" />
      </Box>
    );
  }

  // Empty state -- no workspace-scoped templates yet for this module. Offer
  // an explicit seed action instead of silently hiding the whole section.
  if (templates.length === 0 && !editing) {
    return (
      <Stack spacing={2}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <EmailIcon sx={{ color: "primary.500" }} />
          <Typography level="title-lg">Email Templates</Typography>
        </Box>
        <Sheet
          variant="soft"
          sx={{
            borderRadius: "sm",
            p: 3,
            textAlign: "center",
          }}
        >
          <Typography level="body-sm" sx={{ mb: 2 }}>
            No email templates for the <strong>{moduleFilter}</strong> module
            have been set up for this workspace yet.
          </Typography>
          <Button
            size="sm"
            variant="solid"
            startDecorator={<AddIcon />}
            loading={seeding}
            onClick={handleSeedDefaults}
          >
            Seed defaults
          </Button>
        </Sheet>
        {feedback && (
          <Alert color={feedback.type} size="sm">
            {feedback.message}
          </Alert>
        )}
      </Stack>
    );
  }

  // Edit view
  if (editing) {
    return (
      <Stack spacing={2}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton
            size="sm"
            variant="plain"
            onClick={() => {
              setEditing(null);
              setFeedback(null);
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <EmailIcon sx={{ color: "primary.500" }} />
          <Typography level="title-lg">{editing.display_name}</Typography>
          {!editing.is_system && (
            <Chip size="sm" variant="soft" color="warning">
              customised
            </Chip>
          )}
        </Stack>

        <EmailTemplateEditor
          subjectTemplate={editing.subject_template}
          onSubjectChange={(val) =>
            setEditing((prev) =>
              prev ? { ...prev, subject_template: val } : prev,
            )
          }
          bodyTemplate={editing.body_template}
          onBodyChange={(val) =>
            setEditing((prev) =>
              prev ? { ...prev, body_template: val } : prev,
            )
          }
          variables={editing.available_variables}
          organizationId={organizationId}
          templateKey={editing.template_key}
        />

        {feedback && (
          <Alert color={feedback.type} size="sm">
            {feedback.message}
          </Alert>
        )}

        <Stack direction="row" spacing={1.5}>
          <Button
            size="sm"
            startDecorator={<SaveIcon />}
            loading={saving}
            onClick={handleSave}
          >
            Save
          </Button>
          {!editing.is_system && (
            <Button
              size="sm"
              variant="outlined"
              color="warning"
              startDecorator={<RestoreIcon />}
              onClick={handleReset}
            >
              Reset to Default
            </Button>
          )}
        </Stack>
      </Stack>
    );
  }

  // List view
  return (
    <Stack spacing={2}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <EmailIcon sx={{ color: "primary.500" }} />
        <Typography level="title-lg">Email Templates</Typography>
        <Typography level="body-xs" sx={{ color: "text.tertiary", ml: "auto" }}>
          {templates.length} templates
        </Typography>
      </Box>

      <Divider />

      <Sheet variant="outlined" sx={{ borderRadius: "sm", overflow: "hidden" }}>
        <Table size="sm" stickyHeader>
          <thead>
            <tr>
              <th style={{ width: "40%" }}>Template</th>
              <th style={{ width: "35%" }}>Subject</th>
              <th style={{ width: "15%" }}>Status</th>
              <th style={{ width: "10%" }} />
            </tr>
          </thead>
          <tbody>
            {templates.map((t) => (
              <tr key={t.template_key}>
                <td>
                  <Typography level="body-sm">{t.display_name}</Typography>
                  <Typography
                    level="body-xs"
                    sx={{
                      color: "text.tertiary",
                      fontFamily: "monospace",
                    }}
                  >
                    {t.template_key}
                  </Typography>
                </td>
                <td>
                  <Typography level="body-xs" noWrap>
                    {t.subject_template}
                  </Typography>
                </td>
                <td>
                  {t.is_system ? (
                    <Chip size="sm" variant="soft" color="neutral">
                      default
                    </Chip>
                  ) : (
                    <Chip size="sm" variant="soft" color="warning">
                      customised
                    </Chip>
                  )}
                </td>
                <td>
                  <IconButton
                    size="sm"
                    variant="plain"
                    onClick={() => handleEdit(t.template_key)}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Sheet>

      {feedback && (
        <Alert color={feedback.type} size="sm">
          {feedback.message}
        </Alert>
      )}
    </Stack>
  );
};

export default WorkspaceEmailTemplates;
